import { useEffect, useState } from 'react'

import { initials } from '@/lib/initials'

export interface AssignUserDialogProps {
  open: boolean
  /** Public ref shown in the header, e.g. 'J5H-Q4TV'. */
  refLabel: string
  /** Usually the customer name. */
  contextLabel?: string
  users: Array<{ id: string; name: string }>
  /** The currently assigned user, if any. */
  currentUserId?: string
  onClose: () => void
  /** Awaited before the dialog closes. */
  onConfirm: (userId: string) => void | Promise<void>
}

/**
 * Deliberate technician assignment with the same friction as
 * ChangeStatusDialog: pick a person, confirm.
 */
export function AssignUserDialog({
  open,
  refLabel,
  contextLabel,
  users,
  currentUserId,
  onClose,
  onConfirm,
}: AssignUserDialogProps) {
  const [target, setTarget] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setTarget('')
      setSaving(false)
    }
  }, [open])

  if (!open) return null

  const currentUser = users.find((user) => user.id === currentUserId)
  const targetUser = users.find((user) => user.id === target)
  const confirmEnabled = target !== '' && target !== currentUserId && !saving

  const handleConfirm = async () => {
    if (!confirmEnabled) return
    setSaving(true)
    try {
      await onConfirm(target)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/45 p-6">
      <div className="w-[440px] max-w-full overflow-hidden rounded-[14px] bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-[22px] pt-[18px] pb-3.5">
          <h2 className="m-0 mb-1 text-base font-semibold">Assign technician</h2>
          <p className="m-0 text-[12.5px] text-slate-500">
            <span className="font-mono text-blue-700">{refLabel}</span>
            {contextLabel ? <> · {contextLabel}</> : null}
          </p>
          <div className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span className="text-slate-400">Currently</span>
            <span className="font-semibold text-slate-900">
              {currentUser?.name ?? 'Unassigned'}
            </span>
          </div>
        </div>

        <div className="px-[22px] py-4">
          <div className="mb-2 text-[10.5px] tracking-wider text-slate-400 uppercase">
            Assign to
          </div>
          <div className="flex max-h-[42vh] flex-col gap-1.5 overflow-auto">
            {users.map((user) => {
              const isPicked = user.id === target
              const isCurrent = user.id === currentUserId
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setTarget(user.id)}
                  disabled={isCurrent}
                  className={`flex w-full items-center justify-between rounded-[9px] px-3 py-[9px] text-left text-[12.5px] font-medium ${
                    isPicked
                      ? 'cursor-pointer border-2 border-blue-500 bg-blue-50 text-slate-900'
                      : isCurrent
                        ? 'cursor-default border border-slate-200 bg-slate-50 text-slate-400'
                        : 'cursor-pointer border border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700">
                      {initials(user.name)}
                    </span>
                    {user.name}
                    {isCurrent ? (
                      <span className="text-[10.5px] text-slate-400">current</span>
                    ) : null}
                  </span>
                  <span className="font-bold text-blue-500">{isPicked ? '✓' : ''}</span>
                </button>
              )
            })}
            {users.length === 0 ? (
              <p className="m-0 py-3 text-center text-xs text-slate-400">
                No technicians available.
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-[22px] pt-3 pb-[18px]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-[34px] cursor-pointer rounded-lg border border-slate-300 bg-white px-3.5 text-[12.5px] font-medium text-slate-600"
          >
            Keep as is
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!confirmEnabled}
            className={`inline-flex h-[34px] items-center gap-2 rounded-lg border-none px-4 text-[12.5px] font-semibold ${
              confirmEnabled
                ? 'cursor-pointer bg-slate-900 text-white'
                : 'cursor-not-allowed bg-slate-200 text-slate-400'
            }`}
          >
            {saving ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            {targetUser ? `Assign to ${targetUser.name}` : 'Choose a technician'}
          </button>
        </div>
      </div>
    </div>
  )
}
